import type { FormData } from '../types';

async function captureMap(mapElement: HTMLDivElement): Promise<string | null> {
  try {
    console.log('Iniciando captura do mapa:', { 
      id: mapElement.id,
      width: mapElement.offsetWidth, 
      height: mapElement.offsetHeight,
      visibleImages: mapElement.querySelectorAll('img').length
    });
    
    // Store original display state
    const originalDisplay = mapElement.style.display;
    
    // Ensure map is visible for capture
    mapElement.style.display = 'block';
    
    // Wait for all images to be loaded
    const images = Array.from(mapElement.querySelectorAll('img')) as HTMLImageElement[];
    console.log(`Aguardando carregamento de ${images.length} imagens para ${mapElement.id}...`);
    
    await Promise.all(images.map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve; // Also resolve on error to prevent hanging
      });
    }));

    // Add a small delay to ensure all transitions are complete
    await new Promise(resolve => setTimeout(resolve, 1000));

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) {
      throw new Error('Não foi possível criar o contexto do canvas');
    }

    // Set canvas dimensions
    const rect = mapElement.getBoundingClientRect();
    console.log(`Dimensões do mapa: ${rect.width}x${rect.height}`);
    
    // Usar dimensões fixas se o elemento tiver tamanho zero
    const canvasWidth = rect.width > 0 ? rect.width : 1200;
    const canvasHeight = rect.height > 0 ? rect.height : 900;
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    console.log(`Dimensões do canvas: ${canvas.width}x${canvas.height}`);

    // Draw base image first
    const baseImage = mapElement.querySelector('img') as HTMLImageElement | null;
    if (baseImage) {
      console.log('Processando imagem base:', baseImage.src);
      await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          console.log('Imagem base carregada com sucesso');
          context.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(null);
        };
        img.onerror = (err) => {
          console.error('Erro ao carregar imagem base:', err);
          resolve(null);
        };
        img.src = baseImage.src;
      });
    } else {
      console.warn('Imagem base não encontrada');
    }

    // Draw each layer image
    const layerImages = Array.from(mapElement.querySelectorAll('img:not(:first-child)')) as HTMLImageElement[];
    console.log(`Processando ${layerImages.length} camadas`);
    
    for (const imgElement of layerImages) {
      try {
        console.log('Processando camada:', imgElement.src);
        await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            console.log('Camada carregada com sucesso');
            const computedStyle = window.getComputedStyle(imgElement);
            const opacity = parseFloat(computedStyle.opacity);
            context.globalAlpha = opacity;
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(null);
          };
          img.onerror = (err) => {
            console.error('Erro ao carregar camada:', err, imgElement.src);
            resolve(null);
          };
          img.src = imgElement.src;
        });
      } catch (error) {
        console.error('Error capturing layer:', error);
      }
    }

    // Reset global alpha
    context.globalAlpha = 1.0;

    // Convert canvas to base64 with compression
    const maxWidth = 1200; // Maximum width for the compressed image
    const quality = 0.8;   // Image quality (0.8 = 80% quality) - increased quality

    // Scale down the canvas if it's too large
    if (canvas.width > maxWidth) {
      const scale = maxWidth / canvas.width;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width * scale;
      tempCanvas.height = canvas.height * scale;
      const tempContext = tempCanvas.getContext('2d');
      if (!tempContext) throw new Error('Failed to create temporary canvas context');
      
      tempContext.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);
      
      // Restaurar estilo original
      mapElement.style.display = originalDisplay;
      
      // Verificar se a imagem não está vazia
      const imageData = tempCanvas.toDataURL('image/jpeg', quality).split(',')[1];
      const dataLength = imageData?.length || 0;
      console.log(`Imagem capturada: ${dataLength} bytes`);
      
      if (dataLength < 100) {
        console.warn('Imagem capturada muito pequena, pode estar vazia');
        // Tente usar uma imagem de fallback ou retorne null
        return null;
      }
      
      return imageData;
    }

    // Restaurar estilo original
    mapElement.style.display = originalDisplay;
    
    // Verificar se a imagem não está vazia
    const imageData = canvas.toDataURL('image/jpeg', quality).split(',')[1];
    const dataLength = imageData?.length || 0;
    console.log(`Imagem capturada: ${dataLength} bytes`);
    
    if (dataLength < 100) {
      console.warn('Imagem capturada muito pequena, pode estar vazia');
      // Tente usar uma imagem de fallback ou retorne null;
      return null;
    }
    
    return imageData;
  } catch (error) {
    console.error('Error capturing map:', error);
    throw new Error('Failed to capture map image');
  }
}

// Variáveis para armazenar os mapas entre as chamadas - usando objetos separados para garantir imagens distintas
let storedCityMap: string | null = null;
let storedAreaMap: string | null = null; 
let storedStreetMap: string | null = null;

export async function sendEmail(
  data: { captureMode?: string, selectedLayers: { cityMap: string[], areaMap: string[], streetMap: string[] } },
  serialNumber: string,
  mapRefs: {
    cityMap: HTMLDivElement | null;
    areaMap: HTMLDivElement | null;
    streetMap: HTMLDivElement | null;
  },
  mapsData: any[],
  mapStyles: Record<string, 'color' | 'gray' | 'simple'>
) {
  try {
    console.log('Starting map capture process...', data.captureMode);
    console.log('Map refs received:', {
      hasCityMap: !!mapRefs.cityMap,
      hasAreaMap: !!mapRefs.areaMap,
      hasStreetMap: !!mapRefs.streetMap
    });

    // Registrar o conteúdo atual dos mapas armazenados
    console.log('Conteúdo atual dos mapas armazenados:', {
      cityMapExists: !!storedCityMap,
      cityMapLength: storedCityMap?.length || 0,
      areaMapExists: !!storedAreaMap,
      areaMapLength: storedAreaMap?.length || 0,
      streetMapExists: !!storedStreetMap,
      streetMapLength: storedStreetMap?.length || 0
    });

    // Função para carregar uma imagem estática como fallback
    const loadStaticImageAsBase64 = async (mapType: string): Promise<string | null> => {
      try {
        // Esperar alguns segundos para garantir que os mapas estáticos estejam disponíveis
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Criar um elemento de imagem para carregar o mapa estático
        const img = new Image();
        return new Promise((resolve) => {
          img.onload = () => {
            // Criar um canvas para converter a imagem para base64
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              console.error('Não foi possível criar contexto para imagem estática');
              resolve(null);
              return;
            }
            
            // Desenhar a imagem no canvas
            ctx.drawImage(img, 0, 0);
            
            // Converter para base64
            const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
            console.log(`Imagem estática (${mapType}) carregada: ${base64.length} bytes`);
            resolve(base64);
          };
          
          img.onerror = () => {
            console.error(`Erro ao carregar imagem estática para ${mapType}`);
            resolve(null);
          };
          
          // Carregar a imagem estática com base no tipo de mapa
          if (mapType === 'city') {
            img.src = '/maps/city/base.png';
          } else if (mapType === 'area') {
            img.src = '/maps/area/base.png';
          } else if (mapType === 'street') {
            img.src = '/maps/street/base.png';
          }
        });
      } catch (error) {
        console.error(`Erro ao carregar imagem estática para ${mapType}:`, error);
        return null;
      }
    };

    // Verificar o modo de captura
    const captureMode = data.captureMode || 'all';
    console.log(`Modo de captura: ${captureMode}`);

    // Capture maps sequentially to ensure all maps are included
    const mapImages: {
      cityMap: string | null;
      areaMap: string | null;
      streetMap: string | null;
    } = {
      cityMap: null,
      areaMap: null,
      streetMap: null
    };

    // Variável para armazenar as imagens finais que serão enviadas no email
    let finalMapImages: {
      cityMap: string | null;
      areaMap: string | null;
      streetMap: string | null;
    } = {
      cityMap: null,
      areaMap: null,
      streetMap: null
    };

    // Capture city map
    if (captureMode === 'city' && mapRefs.cityMap) {
      try {
        console.log('Capturing city map...');
        const cityMapImage = await captureMap(mapRefs.cityMap);
        console.log('City map captured successfully, data length:', cityMapImage?.length || 0);
        
        // Armazenar para uso posterior - garantir que é realmente o mapa da cidade
        if (cityMapImage) {
          // Armazenar o mapa com timestamp para identificação
          storedCityMap = cityMapImage;
          console.log('City map stored successfully with length:', storedCityMap.length);
        } else {
          console.log('Tentando carregar imagem estática para city map...');
          storedCityMap = await loadStaticImageAsBase64('city');
        }
      } catch (error) {
        console.error('Error capturing city map:', error);
        // Tentar usar imagem estática como fallback
        storedCityMap = await loadStaticImageAsBase64('city');
      }
      
      // Retornar sem enviar email
      return { success: true, message: 'City map captured successfully' };
    }

    // Capture area map
    if (captureMode === 'area' && mapRefs.areaMap) {
      try {
        console.log('Capturing area map...');
        const areaMapImage = await captureMap(mapRefs.areaMap);
        console.log('Area map captured successfully, data length:', areaMapImage?.length || 0);
        
        // Armazenar para uso posterior - garantir que é realmente o mapa da área
        if (areaMapImage) {
          // Armazenar o mapa com timestamp para identificação
          storedAreaMap = areaMapImage;
          console.log('Area map stored successfully with length:', storedAreaMap.length);
        } else {
          console.log('Tentando carregar imagem estática para area map...');
          storedAreaMap = await loadStaticImageAsBase64('area');
        }
      } catch (error) {
        console.error('Error capturing area map:', error);
        // Tentar usar imagem estática como fallback
        storedAreaMap = await loadStaticImageAsBase64('area');
      }
      
      // Retornar sem enviar email
      return { success: true, message: 'Area map captured successfully' };
    }

    // Capture street map e enviar email
    if (captureMode === 'street_and_send' && mapRefs.streetMap) {
      try {
        console.log('Capturing street map...');
        const streetMapImage = await captureMap(mapRefs.streetMap);
        console.log('Street map captured successfully, data length:', streetMapImage?.length || 0);
        
        // Armazenar para uso posterior - garantir que é realmente o mapa da rua
        if (streetMapImage) {
          // Armazenar o mapa com timestamp para identificação
          storedStreetMap = streetMapImage;
          console.log('Street map stored successfully with length:', storedStreetMap.length);
        } else {
          console.log('Tentando carregar imagem estática para street map...');
          storedStreetMap = await loadStaticImageAsBase64('street');
        }
      } catch (error) {
        console.error('Error capturing street map:', error);
        // Tentar usar imagem estática como fallback
        storedStreetMap = await loadStaticImageAsBase64('street');
      }
      
      // Agora combinar todos os mapas armazenados
      finalMapImages = {
        cityMap: storedCityMap,
        areaMap: storedAreaMap,
        streetMap: storedStreetMap
      };
      
      console.log('Imagens finais distintas para envio:', {
        hasCityMap: !!finalMapImages.cityMap,
        hasAreaMap: !!finalMapImages.areaMap,
        hasStreetMap: !!finalMapImages.streetMap,
        cityMapLength: finalMapImages.cityMap?.length || 0,
        areaMapLength: finalMapImages.areaMap?.length || 0,
        streetMapLength: finalMapImages.streetMap?.length || 0,
        mapsAreDifferent: storedCityMap !== storedAreaMap && storedCityMap !== storedStreetMap && storedAreaMap !== storedStreetMap
      });

      // Verifique as URLs das imagens base dos mapas para depuração
      console.log('Base Image URLs: ', {
        city: mapsData[0]?.baseImageUrl || 'undefined',
        area: mapsData[1]?.baseImageUrl || 'undefined',
        street: mapsData[2]?.baseImageUrl || 'undefined'
      });
      
      // Verifique quais camadas estão ativas para cada mapa
      console.log('Active Layers: ', {
        city: mapsData[0]?.layers.filter(l => l.isActive).map(l => l.id) || [],
        area: mapsData[1]?.layers.filter(l => l.isActive).map(l => l.id) || [],
        street: mapsData[2]?.layers.filter(l => l.isActive).map(l => l.id) || []
      });
    } else if (captureMode === 'all') {
      // Capturar todos os mapas no modo tradicional
      // Capture city map
      if (mapRefs.cityMap) {
        try {
          console.log('Capturing city map...');
          mapImages.cityMap = await captureMap(mapRefs.cityMap);
          console.log('City map captured successfully, data length:', mapImages.cityMap?.length || 0);
          
          // Se a captura falhar, usar imagem estática
          if (!mapImages.cityMap) {
            console.log('Tentando carregar imagem estática para city map...');
            mapImages.cityMap = await loadStaticImageAsBase64('city');
          }
        } catch (error) {
          console.error('Error capturing city map:', error);
          // Tentar usar imagem estática como fallback
          mapImages.cityMap = await loadStaticImageAsBase64('city');
        }
      }

      // Capture area map
      if (mapRefs.areaMap) {
        try {
          console.log('Capturing area map...');
          mapImages.areaMap = await captureMap(mapRefs.areaMap);
          console.log('Area map captured successfully, data length:', mapImages.areaMap?.length || 0);
          
          // Se a captura falhar, usar imagem estática
          if (!mapImages.areaMap) {
            console.log('Tentando carregar imagem estática para area map...');
            mapImages.areaMap = await loadStaticImageAsBase64('area');
          }
        } catch (error) {
          console.error('Error capturing area map:', error);
          // Tentar usar imagem estática como fallback
          mapImages.areaMap = await loadStaticImageAsBase64('area');
        }
      }

      // Capture street map
      if (mapRefs.streetMap) {
        try {
          console.log('Capturing street map...');
          mapImages.streetMap = await captureMap(mapRefs.streetMap);
          console.log('Street map captured successfully, data length:', mapImages.streetMap?.length || 0);
          
          // Se a captura falhar, usar imagem estática
          if (!mapImages.streetMap) {
            console.log('Tentando carregar imagem estática para street map...');
            mapImages.streetMap = await loadStaticImageAsBase64('street');
          }
        } catch (error) {
          console.error('Error capturing street map:', error);
          // Tentar usar imagem estática como fallback
          mapImages.streetMap = await loadStaticImageAsBase64('street');
        }
      }
      
      // Usar as imagens capturadas para o email
      finalMapImages = mapImages;
    }

    // Se não estamos no modo de envio, retornar
    if (captureMode !== 'street_and_send' && captureMode !== 'all') {
      return { success: true, message: 'Map captured successfully' };
    }

    // Limpar as imagens armazenadas após o envio do email
    const resetStoredMaps = () => {
      storedCityMap = null;
      storedAreaMap = null;
      storedStreetMap = null;
    };

    console.log('Map capture process completed:', {
      hasCityMap: !!finalMapImages.cityMap,
      hasAreaMap: !!finalMapImages.areaMap,
      hasStreetMap: !!finalMapImages.streetMap,
      cityMapLength: finalMapImages.cityMap?.length || 0,
      areaMapLength: finalMapImages.areaMap?.length || 0,
      streetMapLength: finalMapImages.streetMap?.length || 0
    });
    console.debug('Selected layers data:', JSON.stringify(data.selectedLayers));
    console.debug('Maps data structure:', JSON.stringify(mapsData.map(m => ({ mapType: m.mapType, layers: m.layers.length }))));

    // Ensure layers are captured in sequence
    const selectedLayers = {
      cityMap: data.selectedLayers.cityMap,
      areaMap: data.selectedLayers.areaMap,
      streetMap: data.selectedLayers.streetMap
    };

    // Create HTML email template
    const emailTemplate = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
            .header { background: #2563eb; color: white; padding: 20px; text-align: center; }
            .serial-number { color: #2563eb; }
            .content { max-width: 800px; margin: 0 auto; padding: 20px; }
            .section { margin-bottom: 30px; background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            .section-title { color: #2d3748; font-size: 20px; font-weight: 600; margin-bottom: 15px; padding: 8px 12px; border-radius: 6px; border-left: 4px solid #2563eb; border-bottom: 2px solid #e2e8f0; }
            .section-subtitle { color: #2d3748; background-color: #f7fafc; font-size: 20px; font-weight: 600; margin-bottom: 15px; padding: 8px 12px; border-radius: 6px; border-left: 4px solid #2563eb; }
            .field { margin-bottom: 15px; }
            .label { font-weight: bold; color: #4a5568; }
            .value { color: #1a202c; }
            .map-section { margin-top: 25px; }
            .map-title { font-size: 18px; color: #2563eb; margin-bottom: 15px; font-weight: bold; }
            .map-image { width: 100%; max-width: 600px; margin: 15px 0; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            .layers-list { margin: 10px 0; padding-left: 20px; }
            .layers-list li { margin-bottom: 5px; color: #4a5568; }
            .footer { text-align: center; margin-top: 30px; padding: 20px; background: #f3f4f6; color: #666; font-size: 14px; }
            .serial-number { font-family: monospace; background: #f8fafc; padding: 5px 10px; border-radius: 4px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Formulário de Avaliação - Rede Ciclável de Lisboa</h1>
            <p>Formulário Número: <span class="serial-number">${serialNumber}</span></p>
          </div>
          <div class="content">
            ${data.comments ? `
              <div class="section">
                <div class="section-title">Comentários</div>
                <p class="value">${data.comments}</p>
              </div>
            ` : ''}

            <div class="section">
              <div class="section-title">Mapas Selecionados</div>
              
              <div class="map-section">
                <div class="map-title">Mapa 1 - Escala Cidade</div>
                ${finalMapImages.cityMap ? 
                  `<img src="data:image/jpeg;base64,${finalMapImages.cityMap}" alt="Mapa da Cidade" class="map-image">` : 
                  '<div class="map-error">Mapa não disponível</div>'}
                <div class="layer-section">
                  <h4 class="section-subtitle">Mapa Base</h4>
                  <ul class="layers-list">
                    <li>${{
                      'color': 'Cor',
                      'gray': 'Cinza',
                      'simple': 'Simplificado'
                    }[mapStyles[mapsData[0].id]] || 'Cor'}</li>
                  </ul>
                </div>
                <div>Camadas selecionadas:</div>
                ${(() => {
                  const grouped = {};
                  (data.selectedLayers.cityMap || [])
                    .map(layerId => mapsData[0]?.layers.find(l => l.id === layerId))
                    .filter(Boolean)
                    .forEach(layer => {
                      const section = (() => {
                        // Map old section names to new ones
                        if (layer.section === 'Outros') return 'Elementos de Suporte';
                        if (layer.section === 'Transportes') return 'Transportes Públicos';
                        if (!layer.section && ['green-zones', 'zone30', 'volume-transito'].includes(layer.id)) return 'Zonas a Destacar';
                        return layer.section || '';
                      })();
                      if (!grouped[section]) grouped[section] = [];
                      grouped[section].push(layer);
                    });

                  return Object.entries(grouped)
                    .map(([section, layers]) => `
                      <div class="layer-section">
                        <h4 class="section-subtitle">${{ 
  'Infraestrutura': 'Infraestrutura',
  'Elementos de Suporte': 'Elementos de Suporte',
  'Zonas a Destacar': 'Zonas a Destacar',
  'Transportes Públicos': 'Transportes Públicos',
  'Texto': 'Texto'
}[section] || section}</h4>
                        <ul class="layers-list">
                          ${layers.map(layer => `<li>${layer.name}</li>`).join('')}
                        </ul>
                      </div>
                    `)
                    .join('');
                })()}
                ${(data.selectedLayers.cityMap || []).length === 0 ? '<p>Nenhuma camada selecionada</p>' : ''}
                </ul>
              </div>

              <div class="map-section">
                <div class="map-title">Mapa 2 - Escala Bairro/Freguesia</div>
                ${finalMapImages.areaMap ? 
                  `<img src="data:image/jpeg;base64,${finalMapImages.areaMap}" alt="Mapa da Área" class="map-image">` : 
                  '<div class="map-error">Mapa não disponível</div>'}
                <div class="layer-section">
                  <h4 class="section-subtitle">Mapa Base</h4>
                  <ul class="layers-list">
                    <li>${{
                      'color': 'Cor',
                      'gray': 'Cinza',
                      'simple': 'Simplificado'
                    }[mapStyles[mapsData[1].id]] || 'Cor'}</li>
                  </ul>
                </div>
                <div>Camadas selecionadas:</div>
                ${(() => {
                  const grouped = {};
                  (data.selectedLayers.areaMap || [])
                    .map(layerId => mapsData[1]?.layers.find(l => l.id === layerId))
                    .filter(Boolean)
                    .forEach(layer => {
                      const section = (() => {
                        // Map old section names to new ones
                        if (layer.section === 'Outros') return 'Elementos de Suporte';
                        if (layer.section === 'Transportes') return 'Transportes Públicos';
                        if (!layer.section && ['green-zones', 'zone30', 'volume-transito'].includes(layer.id)) return 'Zonas a Destacar';
                        return layer.section || '';
                      })();
                      if (!grouped[section]) grouped[section] = [];
                      grouped[section].push(layer);
                    });

                  return Object.entries(grouped)
                    .map(([section, layers]) => `
                      <div class="layer-section">
                        <h4 class="section-subtitle">${{ 
  'Infraestrutura': 'Infraestrutura',
  'Elementos de Suporte': 'Elementos de Suporte',
  'Zonas a Destacar': 'Zonas a Destacar',
  'Transportes Públicos': 'Transportes Públicos',
  'Texto': 'Texto'
}[section] || section}</h4>
                        <ul class="layers-list">
                          ${layers.map(layer => `<li>${layer.name}</li>`).join('')}
                        </ul>
                      </div>
                    `)
                    .join('');
                })()}
                ${(data.selectedLayers.areaMap || []).length === 0 ? '<p>Nenhuma camada selecionada</p>' : ''}
                </ul>
              </div>

              <div class="map-section">
                <div class="map-title">Mapa 3 - Escala Rua</div>
                ${finalMapImages.streetMap ? 
                  `<img src="data:image/jpeg;base64,${finalMapImages.streetMap}" alt="Mapa da Rua" class="map-image">` : 
                  '<div class="map-error">Mapa não disponível</div>'}
                <div class="layer-section">
                  <h4 class="section-subtitle">Mapa Base</h4>
                  <ul class="layers-list">
                    <li>${{
                      'color': 'Cor',
                      'gray': 'Cinza',
                      'simple': 'Simplificado'
                    }[mapStyles[mapsData[2].id]] || 'Cor'}</li>
                  </ul>
                </div>
                <div>Camadas selecionadas:</div>
                ${(() => {
                  const grouped = {};
                  (data.selectedLayers.streetMap || [])
                    .map(layerId => mapsData[2]?.layers.find(l => l.id === layerId))
                    .filter(Boolean)
                    .forEach(layer => {
                      const section = (() => {
                        // Map old section names to new ones
                        if (layer.section === 'Outros') return 'Elementos de Suporte';
                        if (layer.section === 'Transportes') return 'Transportes Públicos';
                        if (!layer.section && ['green-zones', 'zone30', 'volume-transito'].includes(layer.id)) return 'Zonas a Destacar';
                        return layer.section || '';
                      })();
                      if (!grouped[section]) grouped[section] = [];
                      grouped[section].push(layer);
                    });

                  return Object.entries(grouped)
                    .map(([section, layers]) => `
                      <div class="layer-section">
                        <h4 class="section-subtitle">${{ 
  'Infraestrutura': 'Infraestrutura',
  'Elementos de Suporte': 'Elementos de Suporte',
  'Zonas a Destacar': 'Zonas a Destacar',
  'Transportes Públicos': 'Transportes Públicos',
  'Texto': 'Texto'
}[section] || section}</h4>
                        <ul class="layers-list">
                          ${layers.map(layer => `<li>${layer.name}</li>`).join('')}
                        </ul>
                      </div>
                    `)
                    .join('');
                })()}
                ${(data.selectedLayers.streetMap || []).length === 0 ? '<p>Nenhuma camada selecionada</p>' : ''}
                </ul>
              </div>
            </div>

            ${data.comments ? `
              <div class="section">
                <div class="section-title">Comentários</div>
                <p class="value">${data.comments}</p>
              </div>
            ` : ''}

            <div class="footer">
              <p>Este é um email automático. Por favor não responda.</p>
              <p style="font-size: 12px; color: #666;">
                IP do Remetente: ${window.location.hostname}<br>
                Data de Submissão: ${new Date().toLocaleString('pt-PT')}<br>
                Número de Série: ${serialNumber}
              </p>
            </div>
          </div>
        </body>
      </html>
    `;

    console.log('Preparing to send email with maps:', {
      hasCityMap: !!finalMapImages.cityMap,
      hasAreaMap: !!finalMapImages.areaMap,
      hasStreetMap: !!finalMapImages.streetMap
    });

    // Depois de enviar o email, imprimir o log final
    console.log('Preparando para enviar email com maps:', {
      hasCityMap: !!finalMapImages.cityMap,
      hasAreaMap: !!finalMapImages.areaMap,
      hasStreetMap: !!finalMapImages.streetMap,
      mapsAreDifferent: 
        finalMapImages.cityMap !== finalMapImages.areaMap && 
        finalMapImages.cityMap !== finalMapImages.streetMap && 
        finalMapImages.areaMap !== finalMapImages.streetMap
    });

    // Criar um hash parcial para comparação
    const createHash = (str: string | null): string => {
      if (!str) return "null";
      return str.substring(0, 20) + '...';
    };

    console.log('Comparação de hashes:', {
      cityHash: createHash(finalMapImages.cityMap),
      areaHash: createHash(finalMapImages.areaMap),
      streetHash: createHash(finalMapImages.streetMap)
    });

    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: ['pjbaiao@me.com', 'ritamartelinho@icloud.com'],
        from: 'contact@ritamartelo.com',
        subject: `Submissão de Formulário - Rede Ciclável de Lisboa (${serialNumber})`,
        formData: data,
        serialNumber,
        mapImages: finalMapImages,
        emailTemplate
      })
    });

    const responseData = await response.json();

    if (!responseData.success) {
      console.error('Server response error:', responseData);
      throw new Error(responseData.error || 'Error sending email');
    }

    console.log('Email sent successfully with serial number:', serialNumber);
    
    // Limpar as imagens armazenadas após o envio bem-sucedido
    resetStoredMaps();

    return responseData;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to send email: ${error.message}`);
    }
    throw new Error('Unknown error while sending email');
  }
}